<script setup>
import { computed, ref, watch, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import wootConstants from 'dashboard/constants/globals';
import { INBOX_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

import InboxCard from 'dashboard/components-next/Inbox/InboxCard.vue';
import InboxListHeader from './components/InboxListHeader.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import TolaSignal from 'dashboard/components-next/empty-state/TolaSignal.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { uiSettings } = useUISettings();

const notificationList = ref(null);
const page = ref(1);
const status = ref('');
const type = ref('');
const sortOrder = ref(wootConstants.INBOX_SORT_BY.NEWEST);
const isInboxContextMenuOpen = ref(false);
const hasFetchError = ref(false);

const infiniteLoaderOptions = computed(() => ({
  root: notificationList.value,
  rootMargin: '100px 0px 100px 0px',
}));

const meta = useMapGetter('notifications/getMeta');
const uiFlags = useMapGetter('notifications/getUIFlags');
const records = useMapGetter('notifications/getFilteredNotificationsV4');
const inboxById = useMapGetter('inboxes/getInboxById');

const currentConversationId = computed(() => Number(route.params.id));

const inboxFilters = computed(() => ({
  page: page.value,
  status: status.value,
  type: type.value,
  sortOrder: sortOrder.value,
}));

const notifications = computed(() => {
  return records.value(inboxFilters.value);
});

const showEndOfList = computed(() => {
  return uiFlags.value.isAllNotificationsLoaded && !uiFlags.value.isFetching;
});

const showEmptyState = computed(() => {
  return (
    !uiFlags.value.isFetching &&
    !hasFetchError.value &&
    !notifications.value.length
  );
});

const stateInbox = inboxId => {
  return inboxById.value(inboxId);
};

const fetchNotifications = async () => {
  page.value = 1;
  hasFetchError.value = false;
  store.dispatch('notifications/clear');
  const filter = inboxFilters.value;
  const loaded = await store.dispatch('notifications/index', filter);
  hasFetchError.value = loaded === false;
};

const scrollActiveIntoView = () => {
  const activeEl = notificationList.value?.querySelector('.inbox-card.active');
  activeEl?.scrollIntoView({ block: 'center', behavior: 'smooth' });
};

const redirectToInbox = () => {
  if (route.name === 'inbox_view') return;
  router.replace({ name: 'inbox_view' });
};

const loadMoreNotifications = async () => {
  if (uiFlags.value.isAllNotificationsLoaded) return;

  page.value += 1;
  const loaded = await store.dispatch('notifications/index', {
    page: page.value,
    status: status.value,
    type: type.value,
    sortOrder: sortOrder.value,
  });
  if (loaded === false) {
    page.value -= 1;
    hasFetchError.value = true;
  }
};

const focusRelativeNotification = (event, offset) => {
  const cards = Array.from(
    notificationList.value?.querySelectorAll('.inbox-card') ?? []
  );
  const currentCard = event.target.closest('.inbox-card');
  const currentIndex = cards.indexOf(currentCard);
  const targetIndex = Math.min(
    Math.max(currentIndex + offset, 0),
    cards.length - 1
  );

  cards[targetIndex]?.focus();
};

const markNotificationAsRead = async notificationItem => {
  useTrack(INBOX_EVENTS.MARK_NOTIFICATION_AS_READ);

  const { id, primaryActorId, primaryActorType } = notificationItem;
  try {
    await store.dispatch('notifications/read', {
      id,
      primaryActorId,
      primaryActorType,
      unreadCount: meta.value.unreadCount,
    });

    useAlert(t('INBOX.ALERTS.MARK_AS_READ'));
    store.dispatch('notifications/unReadCount');
  } catch {
    useAlert(t('INBOX.ALERTS.MARK_AS_READ_ERROR'));
  }
};

const markNotificationAsUnRead = async notificationItem => {
  useTrack(INBOX_EVENTS.MARK_NOTIFICATION_AS_UNREAD);
  redirectToInbox();

  const { id } = notificationItem;

  try {
    await store.dispatch('notifications/unread', { id });
    useAlert(t('INBOX.ALERTS.MARK_AS_UNREAD'));
    store.dispatch('notifications/unReadCount');
  } catch {
    useAlert(t('INBOX.ALERTS.MARK_AS_UNREAD_ERROR'));
  }
};

const deleteNotification = async notificationItem => {
  useTrack(INBOX_EVENTS.DELETE_NOTIFICATION);
  redirectToInbox();

  try {
    await store.dispatch('notifications/delete', {
      notification: notificationItem,
      unreadCount: meta.value.unreadCount,
      count: meta.value.count,
    });

    useAlert(t('INBOX.ALERTS.DELETE'));
  } catch {
    useAlert(t('INBOX.ALERTS.DELETE_ERROR'));
  }
};

const onFilterChange = option => {
  const { STATUS, TYPE, SORT_ORDER } = wootConstants.INBOX_FILTER_TYPE;
  if (option.type === STATUS) {
    status.value = option.selected ? option.key : '';
  }
  if (option.type === TYPE) {
    type.value = option.selected ? option.key : '';
  }
  if (option.type === SORT_ORDER) {
    sortOrder.value = option.key;
  }
  fetchNotifications();
};

const setSavedFilter = () => {
  const { inbox_filter_by: filterBy = {} } = uiSettings.value;
  const { status: savedStatus, type: savedType, sort_by: sortBy } = filterBy;
  status.value = savedStatus;
  type.value = savedType;
  sortOrder.value = sortBy || wootConstants.INBOX_SORT_BY.NEWEST;
  store.dispatch('notifications/setNotificationFilters', inboxFilters.value);
};

const openConversation = async notificationItem => {
  const {
    id,
    primaryActorId,
    primaryActorType,
    primaryActor: { inboxId, id: conversationId },
    notificationType,
  } = notificationItem;

  if (route.params.id === String(conversationId)) return;

  useTrack(INBOX_EVENTS.OPEN_CONVERSATION_VIA_INBOX, {
    notificationType,
  });

  try {
    await store.dispatch('notifications/read', {
      id,
      primaryActorId,
      primaryActorType,
      unreadCount: meta.value.unreadCount,
    });

    // to update the unread count in the store realtime
    store.dispatch('notifications/unReadCount');
  } catch {
    useAlert(t('INBOX.ALERTS.MARK_AS_READ_ERROR'));
  }

  // Marking the notification as read is bookkeeping, opening the conversation
  // is the intent, so navigate even when the read dispatch fails.
  router.push({
    name: 'inbox_view_conversation',
    params: { inboxId, type: 'conversation', id: conversationId },
  });
};

watch(
  inboxFilters,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      store.dispatch('notifications/updateNotificationFilters', newVal);
    }
  },
  { deep: true }
);

watch(currentConversationId, () => {
  nextTick(scrollActiveIntoView);
});

onMounted(() => {
  scrollActiveIntoView();
  setSavedFilter();
  fetchNotifications();
});
</script>

<template>
  <section class="flex w-full h-full min-w-0 bg-n-surface-1">
    <div
      class="flex flex-col h-full w-full lg:min-w-[360px] lg:max-w-[380px] 2xl:min-w-[400px] 2xl:max-w-[400px] ltr:border-r rtl:border-l border-n-weak bg-n-solid-2"
      :class="!currentConversationId ? 'flex' : 'hidden xl:flex'"
    >
      <InboxListHeader
        :is-context-menu-open="isInboxContextMenuOpen"
        :total-count="meta.count"
        :unread-count="meta.unreadCount"
        @filter="onFilterChange"
        @redirect="redirectToInbox"
      />
      <div
        ref="notificationList"
        role="listbox"
        :aria-label="$t('INBOX.WORKSPACE.QUEUE_LABEL')"
        class="flex flex-col gap-1.5 w-full flex-1 min-h-0 p-2 overflow-x-hidden overflow-y-auto"
        @keydown.up.prevent="focusRelativeNotification($event, -1)"
        @keydown.down.prevent="focusRelativeNotification($event, 1)"
      >
        <div
          v-if="hasFetchError"
          role="alert"
          class="flex flex-col items-start gap-2 p-3 border rounded-xl border-n-ruby-5 bg-n-ruby-3 text-n-ruby-12"
        >
          <span class="flex items-start gap-2 text-xs">
            <span
              class="mt-0.5 i-lucide-wifi-off size-3.5 shrink-0"
              aria-hidden="true"
            />
            {{ $t('INBOX.WORKSPACE.ERROR.LIST') }}
          </span>
          <Button
            :label="$t('INBOX.WORKSPACE.RETRY')"
            icon="i-lucide-refresh-cw"
            color="ruby"
            variant="outline"
            size="xs"
            @click="fetchNotifications"
          />
        </div>
        <InboxCard
          v-for="notificationItem in notifications"
          :key="notificationItem.id"
          :inbox-item="notificationItem"
          :state-inbox="stateInbox(notificationItem.primaryActor?.inboxId)"
          :is-active="
            currentConversationId === notificationItem.primaryActor?.id
          "
          class="inbox-card"
          :class="{
            active: currentConversationId === notificationItem.primaryActor?.id,
          }"
          @mark-notification-as-read="markNotificationAsRead"
          @mark-notification-as-un-read="markNotificationAsUnRead"
          @delete-notification="deleteNotification"
          @context-menu-open="isInboxContextMenuOpen = true"
          @context-menu-close="isInboxContextMenuOpen = false"
          @click="openConversation(notificationItem)"
        />
        <div
          v-if="uiFlags.isFetching"
          role="status"
          aria-busy="true"
          :aria-label="$t('SKELETON.LOADING')"
          class="flex flex-col gap-0.5"
        >
          <div
            v-for="row in 3"
            :key="row"
            class="flex items-center gap-3 px-3 py-4"
          >
            <Skeleton variant="circle" :announce="false" class="size-8" />
            <Skeleton
              variant="text"
              :lines="2"
              :announce="false"
              class="flex-1"
            />
          </div>
        </div>
        <p
          v-if="showEmptyState"
          class="flex flex-col items-center justify-center flex-1 gap-2 p-6 text-sm text-center text-n-slate-10"
        >
          <TolaSignal icon="i-lucide-inbox" />
          <strong class="text-n-slate-12">
            {{ $t('INBOX.WORKSPACE.EMPTY.TITLE') }}
          </strong>
          <span>{{ $t('INBOX.WORKSPACE.EMPTY.DETAIL') }}</span>
        </p>
        <IntersectionObserver
          v-if="!showEndOfList && !uiFlags.isFetching"
          :options="infiniteLoaderOptions"
          @observed="loadMoreNotifications"
        />
      </div>
    </div>
    <router-view />
    <CmdBarConversationSnooze />
  </section>
</template>
